import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

const PAGE_SIZE_STORAGE_KEY = 'companion-rules-page-size';
const DEFAULT_PAGE_SIZE = 20;
const MIN_PAGE_SIZE = 10;
const MAX_PAGE_SIZE = 100;

export const useCompanionRulesUrlParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const isAppCompanionTab = useMemo(
    () => searchParams.get('tab') === 'app-companion',
    [searchParams]
  );

  const storedPageSize = useMemo(() => {
    try {
      const stored = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
      const parsed = stored ? parseInt(stored, 10) : DEFAULT_PAGE_SIZE;
      return isNaN(parsed)
        ? DEFAULT_PAGE_SIZE
        : Math.min(Math.max(MIN_PAGE_SIZE, parsed), MAX_PAGE_SIZE);
    } catch {
      return DEFAULT_PAGE_SIZE;
    }
  }, []);

  const page = useMemo(() => {
    const rawPage = parseInt(searchParams.get('page') || '1', 10);
    return Math.max(1, isNaN(rawPage) ? 1 : rawPage);
  }, [searchParams]);

  const pageSize = useMemo(() => {
    const rawPageSize = parseInt(searchParams.get('pageSize') || storedPageSize.toString(), 10);
    return isNaN(rawPageSize)
      ? DEFAULT_PAGE_SIZE
      : Math.min(Math.max(MIN_PAGE_SIZE, rawPageSize), MAX_PAGE_SIZE);
  }, [searchParams, storedPageSize]);

  const search = searchParams.get('search') || '';

  const updateParams = useCallback(
    (updater: (params: URLSearchParams) => void) => {
      setSearchParams(
        prev => {
          const params = new URLSearchParams(prev);
          updater(params);
          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  useEffect(() => {
    if (!isAppCompanionTab) {
      const hasCompanionParams =
        searchParams.get('page') || searchParams.get('pageSize') || searchParams.get('search');

      if (hasCompanionParams) {
        updateParams(params => {
          params.delete('page');
          params.delete('pageSize');
          params.delete('search');
        });
      }
    }
  }, [isAppCompanionTab, searchParams, updateParams]);

  useEffect(() => {
    if (
      isAppCompanionTab &&
      !searchParams.get('pageSize') &&
      storedPageSize !== DEFAULT_PAGE_SIZE
    ) {
      updateParams(params => {
        params.set('pageSize', storedPageSize.toString());
      });
    }
  }, [isAppCompanionTab, searchParams, storedPageSize, updateParams]);

  const setPage = useCallback(
    (newPage: number) => {
      const validPage = Math.max(1, newPage);
      updateParams(params => params.set('page', validPage.toString()));
    },
    [updateParams]
  );

  const setPageSize = useCallback(
    (newPageSize: number) => {
      const validPageSize = Math.min(Math.max(MIN_PAGE_SIZE, newPageSize), MAX_PAGE_SIZE);

      try {
        localStorage.setItem(PAGE_SIZE_STORAGE_KEY, validPageSize.toString());
      } catch (error) {
        console.warn('Failed to save page size to localStorage:', error);
      }

      updateParams(params => {
        params.set('pageSize', validPageSize.toString());
        params.set('page', '1');
      });
    },
    [updateParams]
  );

  const setSearch = useCallback(
    (searchQuery: string) => {
      updateParams(params => {
        if (searchQuery.trim()) {
          params.set('search', searchQuery.trim());
        } else {
          params.delete('search');
        }
        params.set('page', '1');
      });
    },
    [updateParams]
  );

  const setPageAndSize = useCallback(
    (newPage: number, newPageSize: number) => {
      const validPage = Math.max(1, newPage);
      const validPageSize = Math.min(Math.max(MIN_PAGE_SIZE, newPageSize), MAX_PAGE_SIZE);

      try {
        localStorage.setItem(PAGE_SIZE_STORAGE_KEY, validPageSize.toString());
      } catch (error) {
        console.warn('Failed to save page size to localStorage:', error);
      }

      updateParams(params => {
        params.set('page', validPage.toString());
        params.set('pageSize', validPageSize.toString());
      });
    },
    [updateParams]
  );

  return {
    page,
    pageSize,
    search,
    setPage,
    setPageSize,
    setSearch,
    setPageAndSize,
  };
};
